import type { SchemaToType } from '@main/schema-utils';
import { mkInput, mkType } from '@main/schema-utils';
import { PluginType } from '@main/sombra-types';

import {
  DiscoClassScanAction,
  DiscoClassScanStatus,
  DiscoClassScanType,
} from './enums/discoClassScan';
import { UpdatePluginInput } from './plugin';

export const DiscoClassScanPreview = mkType({
  name: 'DiscoClassScanPreview',
  comment: 'The latest disco class scan info',
  fields: {
    type: {
      comment: 'The scan type',
      type: { DiscoClassScanType },
    },
    status: {
      comment: 'The status of the scan',
      type: { DiscoClassScanStatus },
    },
    startedAt: {
      comment: 'When the scan started',
      type: 'Date',
    },
    completedAt: {
      comment: 'When the scan completed',
      type: 'Date',
      optional: true,
    },
  },
});

/** Override type */
export type DiscoClassScanPreview = SchemaToType<typeof DiscoClassScanPreview>;

export const DiscoClassScanConfig = mkType({
  name: 'DiscoClassScanConfig',
  comment: 'A configuration for a disco class scan',
  fields: {
    id: {
      comment: 'The ID of the disco class scan config',
      type: 'id',
      modelName: 'discoClassScanConfig',
    },
    dataSiloId: {
      comment: 'The ID of the data silo this scan belongs to',
      type: 'id',
      modelName: 'dataSilo',
    },
    type: {
      comment: 'The scan type',
      type: { DiscoClassScanType },
    },
    enabled: {
      comment: 'If the scan is enabled',
      type: 'boolean',
    },
    scheduleFrequency: {
      comment: 'How frequently the scan should be run in milliseconds',
      type: 'int',
    },
    lastDiscoClassScanId: {
      comment: 'The ID of the last disco class scan',
      type: 'id',
      modelName: 'discoClassScan',
      optional: true,
    },
    scheduleStartAt: {
      comment: 'When the next scan should start (null if disabled)',
      type: 'Date',
      optional: true,
    },
    lastDiscoClassScan: {
      comment: 'The last disco class scan',
      type: DiscoClassScanPreview,
      optional: true,
    },
  },
});

/** Override type */
export type DiscoClassScanConfig = SchemaToType<typeof DiscoClassScanConfig>;

export const DiscoClassScanConfigQueryInput = mkInput({
  name: 'DiscoClassScanConfigQueryInput',
  comment: 'Input for a disco class scan config query',
  fields: {
    dataSiloId: {
      comment: 'The ID of the data silo to query',
      type: 'id',
      modelName: 'dataSilo',
    },
  },
});

/** Override type */
export type DiscoClassScanConfigQueryInput = SchemaToType<
  typeof DiscoClassScanConfigQueryInput
>;

export const DiscoClassScan = mkType({
  name: 'DiscoClassScan',
  comment: 'A discovery and classification scan',
  fields: {
    id: {
      comment: 'The ID of the disco class scan',
      type: 'id',
      modelName: 'discoClassScan',
    },
    discoClassScanConfig: {
      comment: 'The configuration for the scan',
      type: DiscoClassScanConfig,
    },
    type: DiscoClassScanPreview.fields.type,
    status: DiscoClassScanPreview.fields.status,
    startedAt: DiscoClassScanPreview.fields.startedAt,
    completedAt: DiscoClassScanPreview.fields.completedAt,
  },
});

/** Override type */
export type DiscoClassScan = SchemaToType<typeof DiscoClassScan>;

export const DiscoClassScanFiltersInput = mkInput({
  name: 'DiscoClassScanFiltersInput',
  comment: 'Filters for a disco class scan config query',
  fields: {
    dataSiloId: {
      ...DiscoClassScanConfigQueryInput.fields.dataSiloId,
      optional: true,
    },
    type: {
      ...DiscoClassScan.fields.type,
      optional: true,
    },
    status: {
      ...DiscoClassScan.fields.status,
      optional: true,
    },
    // TODO: https://transcend.height.app/T-45455 - add start and completed range filters
  },
});

/** Override type */
export type DiscoClassScanFiltersInput = SchemaToType<
  typeof DiscoClassScanFiltersInput
>;

export const ScanPluginConfigInput = mkInput({
  name: 'ScanPluginConfigInput',
  comment: 'The configuration for a scan plugin',
  fields: {
    type: {
      comment: 'The type of the plugin',
      type: { PluginType },
    },
    configuration: UpdatePluginInput.fields.configuration,
  },
});

/** Override type */
export type ScanPluginConfigInput = SchemaToType<typeof ScanPluginConfigInput>;

export const UpdateDiscoClassScanConfigInput = mkInput({
  name: 'UpdateDiscoClassScanConfigInput',
  comment: 'Input for updating a disco class scan config',
  fields: {
    id: {
      comment: 'The ID of the disco class scan config to update',
      type: 'id',
      modelName: 'discoClassScanConfig',
    },
    enabled: {
      comment: 'If the scan is enabled',
      type: 'boolean',
      optional: true,
    },
    type: {
      comment: 'The scan type',
      type: { DiscoClassScanType },
      optional: true,
    },
    scheduleFrequency: {
      comment: 'How frequently the scan should be run in milliseconds',
      type: 'int',
      optional: true,
    },
    scheduleStartAt: {
      comment: 'When the next scan should start (null if disabled)',
      type: 'Date',
      optional: true,
    },
    scanPluginConfigs: {
      comment: 'The configurations for the scan plugins',
      type: ScanPluginConfigInput,
      optional: true,
      list: true,
    },
  },
});

/** Override type */
export type UpdateDiscoClassScanConfigInput = SchemaToType<
  typeof UpdateDiscoClassScanConfigInput
>;

export const UpdateDiscoClassScanInput = mkInput({
  name: 'UpdateDiscoClassScanInput',
  comment: 'Input for updating a disco class scan',
  fields: {
    discoClassScanConfigId: {
      comment: 'The ID of the disco class scan config to get the latest scan',
      type: 'id',
      modelName: 'discoClassScanConfig',
    },
    action: {
      comment: 'The action to take on the scan',
      type: { DiscoClassScanAction },
    },
  },
});

/** Override type */
export type UpdateDiscoClassScanInput = SchemaToType<
  typeof UpdateDiscoClassScanInput
>;
